import { Injectable } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { BehaviorSubject, EMPTY, Observable, combineLatest, distinctUntilChanged, of, switchMap } from 'rxjs';
import { FeatureFlagFacade } from 'src/app/core/state/facades/feature-flag.facade';
import { environment } from 'src/environments/environment';
import { VERSION } from 'src/environments/version';

/**
 * This service enables Datadog Real User Monitoring (RUM) to visualize and analyze the real-time performance and user
 * journeys.
 */
@Injectable({
  providedIn: 'root',
})
export class DatadogRumService {
  // For storing initialisation status of DD Rum
  initialised = new BehaviorSubject(false);

  // For storing user session recording status
  recordingStarted = false;

  readonly DATADOG_RUM_CONFIG_DEFAULT = {
    env: environment.environmentName, // The application’s environment.
    version: VERSION.version, // The application’s version.
    forwardErrorsToLogs: true, // Forward console.error logs, uncaught exceptions and network errors to Datadog.
    trackUserInteractions: true, // Enables automatic collection of users actions.
    trackFrustrations: true, // Enables automatic collection of user frustrations. Implies trackUserInteractions: true.
    trackResources: true, // Enables collection of resource events.
    trackLongTasks: true, // Enables collection of long task events.
    defaultPrivacyLevel: 'mask', // The default privacy level applied to data in the user session replays.
    sessionSampleRate: 100, // Percentage of sessions to track.
    sessionReplaySampleRate: 100, // Percentage of user session replays to track.
    telemetrySampleRate: 100, // Telemetry data sample rate about SDK execution sent to Datadog.
  } as const;

  constructor(private featureFlagFacade: FeatureFlagFacade) {}

  /**
   * Initialise Datadog RUM service.
   * This method should be called once on application startup.
   */
  async initialise(): Promise<void> {
    // Check if datadogConfig is present in the current environment object.
    const propertyDescriptor = Object.getOwnPropertyDescriptor(environment, 'datadogConfig');

    if (!this.initialised.value && propertyDescriptor) {
      const datadogConfig = propertyDescriptor.value;

      /**
       * FEATURE FLAG IMPLEMENTATION:
       * KEY: 'core__datadog_rum'
       * DESCRIPTION: Controls the Datadog RUM feature for web frontend.
       */
      const { core__datadog_rum: isDatadogRumEnabled } = await this.featureFlagFacade.isFeatureEnabled([
        'core__datadog_rum',
      ]);

      if (isDatadogRumEnabled) {
        datadogRum.init({
          ...this.DATADOG_RUM_CONFIG_DEFAULT,
          ...datadogConfig.common,
          ...datadogConfig.rum,
        });
        this.initialised.next(true);
      }
    }
  }

  /**
   * For usage inside distinctUntilChanged when comparing two objects.
   * Helps to determine if previous object is equal to the current one.
   *
   * @param previous - object of type [key]: value
   * @param current  - object of type [key]: value
   */
  private isEqual<P extends object, C extends object>(previous: P, current: C): boolean {
    for (const [key, value] of Object.entries(previous)) {
      if (current[key] !== value) return false;
    }
    return true;
  }

  /**
   * Starts User Session Recording when DD Rum is initialised and User is Authenticated and stops
   * Recording when user logs out.
   *
   * @param userAuthStatusSubject - takes user auth subject to determine when user is authenticated.
   */
  manageUserSessionRecording(userAuthStatusSubject: BehaviorSubject<boolean>): Observable<string> {
    return combineLatest({
      isInitialised: this.initialised.asObservable(),
      isAuthenticated: userAuthStatusSubject.asObservable(),
    }).pipe(
      distinctUntilChanged(this.isEqual),
      switchMap(({ isInitialised, isAuthenticated }) => {
        if (isInitialised) {
          // Start recording when user is logged in.
          if (isAuthenticated && !this.recordingStarted) {
            datadogRum.startSessionReplayRecording();
            this.recordingStarted = true;
            return of('[DD Rum] Session Recording Started');
          }

          // Stop recording when user is logged out.
          if (!isAuthenticated && this.recordingStarted) {
            datadogRum.stopSessionReplayRecording();
            this.recordingStarted = false;
            return of('[DD Rum] Session Recording Stopped');
          }

          // Do nothing When user is authenticated and recording already started
          // Or when user is not authenticated and recording is already stopped.
          return EMPTY;
        }
        // Do nothing when DD is not initialised.
        return EMPTY;
      }),
    );
  }
}
